import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowsClockwise, Hash } from "@phosphor-icons/react";

const slug = (s) =>
  String(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const MD = {
  h1: ({ children, ...props }) => (
    <h1 id={slug(String(children))} className="font-heading text-3xl font-medium tracking-tight mt-2 mb-4" {...props}>{children}</h1>
  ),
  h2: ({ children, ...props }) => (
    <h2 id={slug(String(children))} className="font-heading text-xl font-medium tracking-tight mt-10 mb-3 pb-2 border-b border-zinc-200 scroll-mt-20" {...props}>{children}</h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 id={slug(String(children))} className="font-heading text-base font-medium tracking-tight mt-6 mb-2 scroll-mt-20" {...props}>{children}</h3>
  ),
  p: (props) => <p className="text-sm text-zinc-700 leading-6 my-3" {...props} />,
  ul: (props) => <ul className="list-disc pl-5 my-3 text-sm text-zinc-700 space-y-1" {...props} />,
  ol: (props) => <ol className="list-decimal pl-5 my-3 text-sm text-zinc-700 space-y-1" {...props} />,
  li: (props) => <li className="leading-6" {...props} />,
  blockquote: (props) => (
    <blockquote className="border-l-2 border-zinc-300 bg-zinc-50 px-4 py-2 my-4 text-sm text-zinc-600 italic rounded-r" {...props} />
  ),
  code: ({ inline, children, ...props }) => {
    if (inline) {
      return <code className="bg-zinc-100 text-zinc-900 px-1 py-0.5 rounded text-[12.5px] font-mono" {...props}>{children}</code>;
    }
    return (
      <pre className="bg-zinc-950 text-zinc-100 rounded-md p-4 my-4 overflow-auto text-[12.5px] leading-5 font-mono">
        <code {...props}>{children}</code>
      </pre>
    );
  },
  table: (props) => (
    <div className="overflow-auto my-4 rounded-md border border-zinc-200">
      <table className="w-full text-sm" {...props} />
    </div>
  ),
  thead: (props) => <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase tracking-wider" {...props} />,
  th: (props) => <th className="text-left px-3 py-2 font-medium border-b border-zinc-200" {...props} />,
  td: (props) => <td className="px-3 py-2 border-b border-zinc-100 align-top text-zinc-700" {...props} />,
  tr: (props) => <tr className="hover:bg-zinc-50/50" {...props} />,
  hr: () => <hr className="my-8 border-zinc-200" />,
  a: (props) => <a className="text-blue-700 hover:underline" target="_blank" rel="noreferrer" {...props} />,
  strong: (props) => <strong className="font-medium text-zinc-900" {...props} />,
};
const MD_PLUGINS = [remarkGfm];

/**
 * Generic markdown document viewer with sticky table-of-contents.
 *
 *   <MarkdownDoc
 *     title="Specs"
 *     icon={BookOpen}
 *     intro="Living technical specification for MediHub."
 *     loader={api.specs}
 *     testIdPrefix="specs"
 *   />
 */
export default function MarkdownDoc({ title, icon: Icon, intro, loader, testIdPrefix }) {
  const [content, setContent] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [byteSize, setByteSize] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadedOnce = useRef(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await loader();
      setContent(r.content);
      setLastUpdated(r.last_updated);
      setByteSize(r.byte_size);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loadedOnce.current) return;
    loadedOnce.current = true;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toc = useMemo(() => {
    const out = [];
    const re = /^## (.+)$/gm;
    let m;
    while ((m = re.exec(content))) out.push({ id: slug(m[1]), label: m[1].trim() });
    return out;
  }, [content]);

  const lastUpdatedHuman = lastUpdated
    ? new Date(lastUpdated).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : "";

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-page`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-medium tracking-tight flex items-center gap-2">
            {Icon ? <Icon size={22} weight="duotone" /> : null} {title}
          </h2>
          <p className="text-sm text-zinc-500 mt-0.5">
            {intro}{" "}
            {lastUpdated && (
              <>Last updated <strong data-testid={`${testIdPrefix}-last-updated`}>{lastUpdatedHuman}</strong> · {(byteSize / 1024).toFixed(1)} KB</>
            )}
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid={`${testIdPrefix}-refresh`}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50 disabled:opacity-50"
        >
          <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid={`${testIdPrefix}-error`}>
          Could not load document: {error}
        </div>
      )}

      <div className="grid grid-cols-12 gap-6">
        <aside className="col-span-12 lg:col-span-3 order-2 lg:order-1">
          <div className="sticky top-20 rounded-lg border border-zinc-200 bg-white p-3">
            <div className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-2 px-1">On this page</div>
            <nav className="flex flex-col gap-0.5 text-sm max-h-[calc(100vh-200px)] overflow-auto" data-testid={`${testIdPrefix}-toc`}>
              {toc.map((h) => (
                <a
                  key={h.id}
                  href={`#${h.id}`}
                  className="flex items-center gap-1.5 px-2 py-1 rounded text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 truncate"
                  data-testid={`${testIdPrefix}-toc-${h.id}`}
                >
                  <Hash size={11} className="text-zinc-400 shrink-0" />
                  <span className="truncate">{h.label}</span>
                </a>
              ))}
            </nav>
          </div>
        </aside>

        <article className="col-span-12 lg:col-span-9 order-1 lg:order-2">
          <div className="rounded-lg border border-zinc-200 bg-white px-7 py-6" data-testid={`${testIdPrefix}-body`}>
            {loading && !content ? (
              <div className="text-sm text-zinc-500">Loading…</div>
            ) : (
              <ReactMarkdown remarkPlugins={MD_PLUGINS} components={MD}>
                {content}
              </ReactMarkdown>
            )}
          </div>
        </article>
      </div>
    </div>
  );
}
